import React from "react";
import { Clock, MapPin, FileText, Plus, Mail, ArrowRight, PartyPopper, CalendarOff } from "lucide-react";
import { useChantiers } from "../lib/useChantiers";
import { dateEnClair, prochainCours, quand } from "./prochain";
import { Profs } from "./Cartes";
import { AFaire, Annonces, AVenirEcole } from "./AVenir";
import { espaceApi } from "./api";
import { InstallerApp } from "./InstallerApp";
import { choisirEnfant } from "./TabCours";
import { Lien } from "./Lien";
import { MesBillets, billetsDuJour } from "./MesBillets";
import { NosSpectacles } from "./NosSpectacles";

/* Un raccourci rond, comme les actions d'une fiche contact sur iPhone. */
const Raccourci = ({ Icone, label, onClick, href, testId, couleur = "var(--primary)" }) => {
  const contenu = (
    <>
      <span className="w-12 h-12 rounded-full grid place-items-center text-white"
        style={{ background: couleur }}><Icone size={20} strokeWidth={2.2} /></span>
      <span className="text-[12px] font-medium leading-tight">{label}</span>
    </>
  );
  const classes = "flex flex-col items-center gap-1.5 py-1 text-center transition-transform active:scale-95";
  return href
    ? <Lien href={href} className={classes} data-testid={testId}>{contenu}</Lien>
    : <button onClick={onClick} className={classes} data-testid={testId}>{contenu}</button>;
};

/* Les informations envoyées par l'école (infos de stage, rappel de spectacle) : le parent
   les retrouve ici même s'il a perdu l'e-mail.

   École, 20.10.2026 : l'Accueil ne garde que le NOUVEAU — les messages pas encore lus, de
   moins de 30 jours (un message lu reste affiché jusqu'à ce qu'on quitte l'écran). Profil →
   « Messages de l'école » les garde tous (`tous`). Un message lu ici l'est partout. */
const LUS = new Set();
const JOURS_ACCUEIL = 30;
export const estLu = (m) => !!m.lu || LUS.has(m.id);
const recent = (m) => Date.now() - Date.parse(m.le || "") <= JOURS_ACCUEIL * 86400000;
export const nonLus = (messages) => (messages || []).filter((m) => !estLu(m)).length;

export const MessagesEcole = ({ messages, tous = false }) => {
  const [ouvert, setOuvert] = React.useState(null);
  const [lus, setLus] = React.useState([]);
  const affiches = tous ? messages
    : messages.filter((m) => (!estLu(m) && recent(m)) || lus.includes(m.id));
  if (!affiches.length) return null;

  const ouvrir = async (m) => {
    setOuvert(ouvert === m.id ? null : m.id);
    if (!estLu(m) && !lus.includes(m.id)) {
      LUS.add(m.id);
      setLus((x) => [...x, m.id]);
      try { await espaceApi.post(`/messages/${encodeURIComponent(m.id)}/lu`); } catch { /* sans gravité */ }
    }
  };
  const visibles = tous ? affiches : affiches.slice(0, 5);

  return (
    <div className="space-y-3" data-testid={tous ? "espace-messages-tous" : "espace-messages"}>
      {visibles.map((m) => {
        const neuf = !estLu(m) && !lus.includes(m.id);
        return (
          <button key={m.id} onClick={() => ouvrir(m)} data-testid="espace-message"
            className="w-full text-left rounded-3xl p-4 transition-transform active:scale-[0.99]"
            style={{
              background: neuf ? "var(--primary-tint)" : "#fff",
              border: `1px solid ${neuf ? "var(--primary)" : "var(--border)"}`,
            }}>
            <div className="flex items-start gap-3">
              <Mail size={16} className="mt-0.5 shrink-0" style={{ color: "var(--primary)" }} />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold"
                  style={{ color: "var(--primary)" }}>
                  {neuf ? "Nouveau message de l'école" : "Message de l'école"}
                </div>
                <div className="font-semibold text-sm mt-1">{m.sujet}</div>
                {ouvert === m.id && (
                  <div className="text-sm mt-2 leading-relaxed" data-testid="espace-message-corps"
                    dangerouslySetInnerHTML={{ __html: m.html }} />
                )}
                {ouvert === m.id && m.piece && (
                  <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                    Document joint à l'e-mail : {m.piece}
                  </div>
                )}
                <div className="text-[11px] mt-2" style={{ color: "var(--muted)" }}>
                  {String(m.le || "").slice(0, 10).split("-").reverse().join(".")}
                  {ouvert === m.id ? "" : " · toucher pour lire"}
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

/* Le premier écran : ce qu'un parent veut savoir en trois secondes — c'est quand le
   prochain cours, et est-ce que je dois encore payer. Le reste est à un tap. */
export const TabAujourdhui = ({ moi, onOnglet, onAttestation }) => {
  const suivant = prochainCours(moi.enfants);
  const avecAttestation = moi.enfants.filter((e) => e.attestation_lien);
  const chantiers = useChantiers();
  // Le soir du spectacle (ou la veille), les billets passent tout en haut : la famille est à
  // l'entrée, elle doit trouver son QR code sans chercher.
  const billetsEnTete = billetsDuJour(moi.billets);

  return (
    <div className="space-y-5" data-testid="espace-tab-aujourdhui">
      {/* Le même accueil personnel que dans l'app des profs et de la direction. */}
      <h1 className="font-display text-[28px] leading-tight" data-testid="espace-salut">
        {new Date().getHours() < 5 || new Date().getHours() >= 18 ? "Bonsoir" : "Bonjour"}
        {(moi.prenom_responsable || (moi.responsable || "").split(" ")[0]) ? ` ${moi.prenom_responsable || moi.responsable.split(" ")[0]}` : ""} 👋
      </h1>
      {billetsEnTete && <MesBillets billets={moi.billets} />}
      <InstallerApp dejaInstallee={!!moi.app_installee} />
      <MessagesEcole messages={moi.messages || []} />
      {/* Vacances en cours (calendrier des Réglages) : la famille voit tout de suite qu'il n'y
          a pas cours, et quand ça reprend (école, 10.10.2026). */}
      {moi.vacances && (
        <div className="rounded-3xl p-5 bg-white border" style={{ borderColor: "#FDE68A", background: "#FFFBEB" }}
          data-testid="espace-vacances">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#B45309" }}>
            Pas de cours en ce moment
          </div>
          <div className="font-display text-xl leading-tight mt-2" style={{ color: "#0F1B2D" }}>
            {moi.vacances.label}
          </div>
          <div className="text-sm mt-1" style={{ color: "#92400E" }}>
            Reprise des cours le {dateEnClair(moi.vacances.reprise)}.
          </div>
        </div>
      )}
      {suivant ? (
        <div className="rounded-3xl p-5 text-white relative overflow-hidden"
          style={{ background: "linear-gradient(140deg,#0F1B2D 0%,#123243 60%,#00A8AF 100%)" }}
          data-testid="espace-prochain-cours">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold"
            style={{ color: "#66EAF0" }}>
            Prochain cours · {quand(suivant)}
          </div>
          <div className="font-display text-2xl leading-tight mt-2">{suivant.nom}</div>
          <div className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.72)" }}>
            {suivant.enfant.prenom}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-sm">
            <span className="flex items-center gap-1.5">
              <Clock size={14} />
              <span className="font-mono">
                {suivant.heure}{suivant.fin ? `–${suivant.fin}` : ""}
              </span>
            </span>
            {suivant.salle && (
              <span className="flex items-center gap-1.5">
                <MapPin size={14} />
                {/^salle/i.test(suivant.salle) ? suivant.salle : `Salle ${suivant.salle}`}
              </span>
            )}
          </div>
          {suivant.professeur && <Profs texte={suivant.professeur} clair />}
          <button onClick={() => { choisirEnfant(suivant.enfant.id); onOnglet("cours"); }}
            className="mt-4 text-xs underline"
            style={{ color: "#66EAF0" }} data-testid="espace-prevenir-absence">
            {suivant.enfant.prenom} ne pourra pas venir ? Prévenir l'école
          </button>
        </div>
      ) : (
        <div className="card-flat text-center bg-white" data-testid="espace-aucun-cours">
          <PartyPopper size={28} className="mx-auto mb-3" style={{ color: "var(--primary)" }} />
          <div className="font-display text-lg">Aucun cours au programme</div>
          <p className="text-sm text-[var(--muted)] mt-2">
            {moi.verification_requise
              ? moi.message
              : moi.rattachee
                ? "Il n'y a pas d'inscription active en ce moment."
                : "Ton compte est prêt ! Dès ta première inscription, tout apparaît ici."}
          </p>
          {moi.verification_requise ? (
            /* Tant que l'adresse n'est pas confirmée, l'espace reste vide : c'est ce qui
               empêche quelqu'un de voir les enfants et les factures d'une autre famille. */
            <a href="/espace" className="btn-primary mt-5 inline-flex"
              data-testid="espace-a-confirmer">
              J'ai confirmé, ouvrir mon espace <ArrowRight size={16} />
            </a>
          ) : (
            <>
              <Lien href="/inscription" className="btn-primary mt-5 inline-flex"
                data-testid="espace-sinscrire">
                M'inscrire à un cours <ArrowRight size={16} />
              </Lien>
              {/* Nouveau compte (adresse encore inconnue de l'école) : l'essai aussi, et
                  l'adresse à reprendre dans le formulaire — c'est elle qui rattache
                  l'inscription à ce compte. */}
              {!moi.rattachee && (
                <>
                  <div className="mt-3">
                    <Lien href="/inscription?type=essai" className="btn-ghost inline-flex"
                      data-testid="espace-essai">
                      Réserver un cours d'essai
                    </Lien>
                  </div>
                  <p className="text-xs text-[var(--muted)] mt-4" data-testid="espace-adresse-inscription">
                    Inscris-toi avec cette adresse : <strong>{moi.email}</strong> — ton inscription
                    apparaîtra ici toute seule. Déjà inscrit·e avec une autre adresse ? Connecte-toi
                    avec celle-là (Profil → Me déconnecter).
                  </p>
                </>
              )}
            </>
          )}
        </div>
      )}

      <AFaire moi={moi} onOnglet={onOnglet} />
      {!billetsEnTete && <MesBillets billets={moi.billets} />}
      {moi.rattachee && <NosSpectacles moi={moi} billetsOuverts={!!chantiers.spectacles_juin} />}
      <Annonces liste={moi.annonces} />

      <div className="grid grid-cols-4 gap-2 rounded-[22px] bg-white px-2 py-4"
        style={{ border: "1px solid var(--border)" }} data-testid="espace-raccourcis">
        <Raccourci Icone={Plus} label="M'inscrire" testId="espace-raccourci-inscription"
          onClick={() => onOnglet("planning")} />
        <Raccourci Icone={CalendarOff} label="Absence" couleur="#F59E0B"
          testId="espace-raccourci-absence" onClick={() => onOnglet("cours")} />
        {avecAttestation.length > 0 ? (
          <Raccourci Icone={FileText} label="Attestation" couleur="#16A34A"
            testId="espace-raccourci-attestation"
            onClick={() => (avecAttestation.length === 1
              ? onAttestation(avecAttestation[0])
              : onOnglet("cours"))} />
        ) : (
          <Raccourci Icone={FileText} label="Factures" couleur="#16A34A"
            testId="espace-raccourci-factures" onClick={() => onOnglet("factures")} />
        )}
        <Raccourci Icone={Mail} label="Écrire" couleur="#0F1B2D" testId="espace-raccourci-contact"
          href="mailto:info1000arts@gmail.com" />
      </div>

      <AVenirEcole />
    </div>
  );
};
